import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api/client";

type AuthStatus = { authenticated: boolean; kekReady: boolean };

type AuthContextValue = {
  status: AuthStatus | null;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  lockWallets: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.get<AuthStatus>("/api/auth/status");
      setStatus(s);
    } catch {
      setStatus({ authenticated: false, kekReady: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (password: string) => {
      await api.post("/api/auth/login", { password });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout");
    await refresh();
  }, [refresh]);

  const lockWallets = useCallback(async () => {
    try { await api.post("/api/auth/lock"); }
    finally { await refresh(); }
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, login, logout, lockWallets, refresh }),
    [status, login, logout, lockWallets, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
